import { Settings } from 'luxon'
import { getLocale, getP, setLanguage } from 'redux-polyglot'
import api from '../libs/api'
import i18n from '../libs/i18n'
import perf from '../libs/perf'
import { logoutSuccess } from './logout'
import { getAvailableLanguages } from '../selectors/app'
import { GLOBAL_MESSAGE_LAST_UPDATED_KEY } from '../constants/app'

/**
 * Actions for initializing the application
 */
export const INIT_APP = 'APP/INIT_APP'

const initAppRequest = () => ({
  type: INIT_APP,
})

export const initApp = () => dispatch => {
  perf.start('app:init')
  dispatch(initAppRequest())
  dispatch(loadLanguages()).then(() =>
    dispatch(loadTranslations()).then(() =>
      dispatch(loadSettings())
        .then(() => dispatch(deviceNotify(true)))
        .then(() => {
          perf.stop('app:init', 'application initialized')
        })
    )
  )
}

/**
 * Actions for dismissing an error message
 */
export const DISMISS_ERROR = 'APP/DISMISS_ERROR'
export const dismissError = () => ({
  type: DISMISS_ERROR,
})

/**
 * Actions for loading available languages from the API
 */
export const FETCH_LANGUAGES_REQUEST = 'SETTINGS/LANGUAGES/FETCH_REQUEST'
export const FETCH_LANGUAGES_SUCCESS = 'SETTINGS/LANGUAGES/FETCH_SUCCESS'
export const FETCH_LANGUAGES_FAILURE = 'SETTINGS/LANGUAGES/FETCH_FAILURE'

const fetchLanguagesRequest = () => ({
  type: FETCH_LANGUAGES_REQUEST,
})

const fetchLanguagesSuccess = languages => ({
  type: FETCH_LANGUAGES_SUCCESS,
  payload: {
    languages,
  },
})

const fetchLanguagesFailure = () => ({
  type: FETCH_LANGUAGES_FAILURE,
})

export const loadLanguages = () => (dispatch, getState) => {
  perf.start('app:init:i18n')
  dispatch(fetchLanguagesRequest())
  return api
    .getLanguages()
    .then(res => res.json())
    .then(
      response => {
        if (response.status === 200) {
          let defaultLanguage = response.languages.find(
            language => language.default === true
          )
          if (typeof defaultLanguage === 'undefined') {
            defaultLanguage = 'en'
          } else {
            defaultLanguage = defaultLanguage.code
          }
          let currentLanguage = i18n.getCurrentLanguage()
          if (currentLanguage !== null) {
            const currentLanguageValid = response.languages.findIndex(
              language => language.code === currentLanguage
            )
            if (currentLanguageValid === -1) {
              currentLanguage = defaultLanguage
            }
          } else {
            currentLanguage = defaultLanguage
          }
          if (!currentLanguage.startsWith('en')) {
            Settings.defaultLocale = currentLanguage
          }
          return Promise.all([
            dispatch(fetchLanguagesSuccess(response.languages)),
            dispatch(setLanguage(currentLanguage, {})),
          ])
        } else {
          return Promise.all([
            dispatch(
              fetchLanguagesFailure(
                getP(getState()).t('tv_app.error_retrieving_available_languages', {
                  _: 'An error occurred while retrieving available languages',
                })
              )
            ),
            dispatch(setLanguage('en', {})),
          ])
        }
      },
      () =>
        Promise.all([
          dispatch(
            fetchLanguagesFailure(
              getP(getState()).t('tv_app.error_retrieving_available_languages', {
                _: 'An error occurred while retrieving available languages',
              })
            )
          ),
          dispatch(setLanguage('en', {})),
        ])
    )
}

/**
 * Actions for loading translation keys from the back-end
 */
export const FETCH_TRANSLATIONS_REQUEST = 'SETTINGS/TRANSLATIONS/FETCH_REQUEST'
export const FETCH_TRANSLATIONS_SUCCESS = 'SETTINGS/TRANSLATIONS/FETCH_SUCCESS'
export const FETCH_TRANSLATIONS_FAILURE = 'SETTINGS/TRANSLATIONS/FETCH_FAILURE'

const fetchTranslationsRequest = () => ({
  type: FETCH_TRANSLATIONS_REQUEST,
})
const fetchTranslationsSuccess = () => ({
  type: FETCH_TRANSLATIONS_SUCCESS,
})
const fetchTranslationsFailure = () => ({
  type: FETCH_TRANSLATIONS_FAILURE,
})

export const loadTranslations = () => (dispatch, getState) => {
  dispatch(fetchTranslationsRequest())
  return api
    .getTranslations()
    .then(res => res.json())
    .then(
      response => {
        if (response.status === 200) {
          for (let section in response.dictionary) {
            for (let key in response.dictionary[section]) {
              response.dictionary[section][key] = response.dictionary[section][
                key
              ].replace(/#([a-z0-9_]+)#/g, '%{$1}')
            }
          }
          perf.stop(
            'app:init:i18n',
            'translations fetched from server; set up internationalization support'
          )
          return Promise.all([
            dispatch(fetchTranslationsSuccess()),
            dispatch(setLanguage(getLocale(getState()), response.dictionary)),
          ])
        } else {
          return dispatch(fetchTranslationsFailure())
        }
      },
      () => dispatch(fetchTranslationsFailure())
    )
}

/**
 * Actions for fetching application setting from API
 */
export const FETCH_SETTINGS_REQUEST = 'SETTINGS/FETCH_SETTINGS_REQUEST'
export const FETCH_SETTINGS_SUCCESS = 'SETTINGS/FETCH_SETTINGS_SUCCESS'
export const FETCH_SETTINGS_FAILURE = 'SETTINGS/FETCH_SETTINGS_FAILURE'

const fetchSettingsRequest = () => ({
  type: FETCH_SETTINGS_REQUEST,
})

const fetchSettingsSuccess = (settings, t) => ({
  type: FETCH_SETTINGS_SUCCESS,
  payload: {
    settings: {
      ...settings,
      main_menu: {
        items: [
          ...settings.main_menu.items,
          {
            id: '10001',
            name: t('tv_menu.menu_youtube', { _: 'YouTube' }),
            icon:
              'https://www.tikilive.com/public/files/applications/icons/main_menu_channels.png',
            action_target: 'youtube',
            action_id: '0',
            show_is_premium: null,
            video_is_premium: null,
          },
        ],
      },
    },
  },
})

const fetchSettingsFailure = error => ({
  type: FETCH_SETTINGS_FAILURE,
  payload: error instanceof Error ? error : new Error(error),
  error: true,
})

export const loadSettings = () => (dispatch, getState) => {
  perf.start('app:init:settings')
  dispatch(fetchSettingsRequest())
  return api
    .getSettings()
    .then(res => res.json())
    .then(
      response => {
        if (response.status === 200) {
          perf.stop('app:init:settings', 'retrieved app settings')
          return dispatch(fetchSettingsSuccess(response.settings, getP(getState()).t))
        } else {
          return dispatch(fetchSettingsFailure(response.reason))
        }
      },
      () =>
        dispatch(
          fetchSettingsFailure(
            getP(getState()).t('tv_app.error_retrieving_application_settings', {
              _: 'An error occurred while retrieving application settings',
            })
          )
        )
    )
}

/**
 * Actions for registering a device
 */
export const DEVICE_NOTIFY_REQUEST = 'NOTIFY/DEVICE_NOTIFY_REQUEST'
export const DEVICE_NOTIFY_SUCCESS = 'NOTIFY/DEVICE_NOTIFY_SUCCESS'
export const DEVICE_NOTIFY_FAILURE = 'NOTIFY/DEVICE_NOTIFY_FAILURE'
export const DEVICE_NOTIFY_WARNING = 'NOTIFY/DEVICE_NOTIFY_WARNING'

const deviceNotifyRequest = (resetReady = false) => ({
  type: DEVICE_NOTIFY_REQUEST,
  payload: {
    resetReady,
  },
})

const deviceNotifySuccess = () => ({
  type: DEVICE_NOTIFY_SUCCESS,
})

const deviceNotifyForceLogin = () => ({
  type: DEVICE_NOTIFY_SUCCESS,
})

const deviceNotifyForceLogout = () => (dispatch, getState) => {
  dispatch(logoutSuccess())
  dispatch(
    deviceNotifyFailure(
      getP(getState()).t('tv_app.logged_out_automatically', {
        _:
          'You have been automatically logged out\nbecause this device has been removed from your account.',
      })
    )
  )
}

const deviceNotifyRejected = response => ({
  type: DEVICE_NOTIFY_FAILURE,
  payload: new Error(response.reason),
  error: true,
})

const deviceNotifyFailure = error => ({
  type: DEVICE_NOTIFY_FAILURE,
  payload: error instanceof Error ? error : new Error(error),
  error: true,
})

const deviceNotifyWarning = error => ({
  type: DEVICE_NOTIFY_WARNING,
  payload: error instanceof Error ? error : new Error(error),
  error: true,
})

export const deviceNotify = (resetReady = false) => (dispatch, getState) => {
  perf.start('app:init:device')
  dispatch(deviceNotifyRequest(resetReady))
  return api
    .notifyDevice()
    .then(res => res.json())
    .then(
      response => {
        if (response.status === 200) {
          if (
            typeof response.force_logout !== 'undefined' &&
            response.force_logout === true
          ) {
            return dispatch(deviceNotifyForceLogout())
          } else if (
            typeof response.force_login !== 'undefined' &&
            response.force_login === true
          ) {
            return dispatch(deviceNotifyForceLogin())
          } else {
            perf.stop('app:init:device', 'registered device')
            return dispatch(deviceNotifySuccess())
          }
        } else {
          return dispatch(deviceNotifyRejected(response))
        }
      },
      err => {
        if (resetReady) {
          return dispatch(
            deviceNotifyFailure(
              getP(getState()).t('tv_app.error_occurred_registering_device', {
                _: 'An error occurred while registering device',
              })
            )
          )
        } else {
          return dispatch(deviceNotifyWarning(err))
        }
      }
    )
}

/**
 * Actions for showing a global toast message
 */
export const SHOW_TOAST = 'APP/SHOW/TOAST'
export const HIDE_TOAST = 'APP/HIDE/TOAST'

let toastTimeout = null
export const showToast = (message, type = 'success', delay = 3500) => dispatch => {
  if (toastTimeout !== null) {
    clearTimeout(toastTimeout)
    toastTimeout = null
  }
  dispatch({
    type: SHOW_TOAST,
    payload: {
      message,
      type,
    },
  })
  toastTimeout = setTimeout(() => {
    dispatch({
      type: HIDE_TOAST,
    })
    toastTimeout = null
  }, delay)
}

/**
 * Actions for changing the application language
 */
export const SET_CURRENT_LANGUAGE = 'APP/I18N/SET_CURRENT_LANGUAGE'
export const setCurrentLanguage = languageCode => (dispatch, getState) => {
  const availableLanguages = getAvailableLanguages(getState())
  const defaultLanguage = availableLanguages.find(language => language.default === true)
  if (defaultLanguage !== null && defaultLanguage.code === languageCode) {
    i18n.setCurrentLanguage(null)
  } else {
    const isValid = availableLanguages.findIndex(
      language => language.code === languageCode
    )
    if (isValid > -1) {
      i18n.setCurrentLanguage(languageCode)
    }
  }
  dispatch({
    type: SET_CURRENT_LANGUAGE,
    payload: {
      language: languageCode,
    },
  })
}

/**
 * Actions for acknowledging global message
 */
export const ACKNOWLEDGE_GLOBAL_MESSAGE = 'APP/GLOBAL_MESSAGE/ACKNOWLEDGE'
export const acknowledgeGlobalMessage = _ => dispatch => {
  const lastDisplayed = Date.now()
  localStorage.setItem(GLOBAL_MESSAGE_LAST_UPDATED_KEY, lastDisplayed)
  dispatch({
    type: ACKNOWLEDGE_GLOBAL_MESSAGE,
    payload: {
      lastDisplayed,
    },
  })
}

/**
 * Actions for loading available languages from the API
 */
export const FETCH_PRIVACY_POLICY_REQUEST = 'APP/PRIVACY_POLICY/FETCH_REQUEST'
export const FETCH_PRIVACY_POLICY_SUCCESS = 'APP/PRIVACY_POLICY/FETCH_SUCCESS'
export const FETCH_PRIVACY_POLICY_FAILURE = 'APP/PRIVACY_POLICY/FETCH_FAILURE'

const fetchPrivacyPolicyRequest = () => ({
  type: FETCH_PRIVACY_POLICY_REQUEST,
})

const fetchPrivacyPolicySuccess = privacyPolicy => ({
  type: FETCH_PRIVACY_POLICY_SUCCESS,
  payload: {
    privacyPolicy,
  },
})

const fetchPrivacyPolicyFailure = error => ({
  type: FETCH_PRIVACY_POLICY_FAILURE,
  payload: error instanceof Error ? error : new Error(error),
  error: true,
})

export const loadPrivacyPolicy = () => (dispatch, getState) => {
  dispatch(fetchPrivacyPolicyRequest())
  return api
    .getPrivacyPolicy()
    .then(res => res.json())
    .then(
      response => {
        if (response.status === 200) {
          return dispatch(fetchPrivacyPolicySuccess(response.page.content))
        } else {
          return dispatch(fetchPrivacyPolicyFailure(response.reason))
        }
      },
      () =>
        dispatch(
          fetchPrivacyPolicyFailure(
            getP(getState()).t('tv_settings.error_retrieving_privacy_policy', {
              _: 'An error occurred while retrieving the privacy policy',
            })
          )
        )
    )
}

// Actions for terms-of-use
export const FETCH_TERMS_OF_USE_REQUEST = 'APP/TERMS_OF_USE/FETCH_REQUEST'
export const FETCH_TERMS_OF_USE_SUCCESS = 'APP/TERMS_OF_USE/FETCH_SUCCESS'
export const FETCH_TERMS_OF_USE_FAILURE = 'APP/TERMS_OF_USE/FETCH_FAILURE'

const fetchTermsOfUseRequest = () => ({
  type: FETCH_TERMS_OF_USE_REQUEST,
})

const fetchTermsOfUseSuccess = termsOfUse => ({
  type: FETCH_TERMS_OF_USE_SUCCESS,
  payload: {
    termsOfUse,
  },
})

const fetchTermsOfUseFailure = error => ({
  type: FETCH_TERMS_OF_USE_FAILURE,
  payload: error instanceof Error ? error : new Error(error),
  error: true,
})

export const loadTermsOfUse = () => (dispatch, getState) => {
  dispatch(fetchTermsOfUseRequest())
  return api
    .getTermsOfUse()
    .then(res => res.json())
    .then(
      response => {
        if (response.status === 200) {
          return dispatch(fetchTermsOfUseSuccess(response.page.content))
        } else {
          return dispatch(fetchTermsOfUseFailure(response.reason))
        }
      },
      () =>
        dispatch(
          fetchTermsOfUseFailure(
            getP(getState()).t('tv_settings.error_retrieving_terms_of_use', {
              _: 'An error occurred while retrieving the terms of use',
            })
          )
        )
    )
}
